import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import type { Cup, DrinkEntry, WeightEntry } from "../types/models";
import { Button } from "../components/Button";
import { CupButton } from "../components/CupButton";
import { CupEditor } from "../components/CupEditor";
import { EmptyState } from "../components/EmptyState";
import { InputField, TextAreaField } from "../components/Field";
import { IconButton } from "../components/IconButton";
import { Modal } from "../components/Modal";
import { ProgressBar } from "../components/Progress";
import { Skeleton } from "../components/Skeleton";
import { VesselIcon } from "../components/VesselIcon";
import { usePreferences } from "../app/PreferencesProvider";
import { useI18n } from "../i18n/I18nProvider";
import { useToast, type ToastOptions } from "../components/Toast";
import { ApiRequestError } from "../services/api";
import { createIdempotencyKey } from "../lib/idempotency";
import {
  formatTimeInputInTimezone,
  getEffectiveTimezone,
  getLocalDateInTimezone,
  toIsoNow,
  zonedDateTimeToIso
} from "../lib/timezone";
import {
  addDaysToDateInput,
  compareDateInputs,
  formatDateLabel,
  formatTimeLabel,
  isFutureDateInput,
  normalizeApiLocalDate,
  yesterdayInputValue
} from "../lib/dates";
import { preserveEntryVesselSnapshot, resolveEntryVesselKind } from "../lib/entryVessels";
import { formatVolume, weightFromKg, weightToKg } from "../lib/units";
import {
  createCup,
  createDrinkEntry,
  createWeightEntry,
  deleteCup,
  deleteDrinkEntry,
  getHydrationGoals,
  listAllDailyStats,
  listAllDrinkEntries,
  listAllWeightEntries,
  listCups,
  recordDrinkFromCup,
  updateCup,
  updateDrinkEntry
} from "../services/water";
import { getHydrationRecommendation } from "../services/profile";

export function TodayPage() {
  const { t, locale, translateError } = useI18n();
  const { preferences } = usePreferences();
  const { showToast } = useToast();
  const [cups, setCups] = useState<Cup[]>([]);
  const [entries, setEntries] = useState<DrinkEntry[]>([]);
  const [weights, setWeights] = useState<WeightEntry[]>([]);
  const [goalMl, setGoalMl] = useState(2500);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCup, setEditingCup] = useState<Cup | null>(null);
  const [editingEntry, setEditingEntry] = useState<DrinkEntry | null>(null);
  const [weightValue, setWeightValue] = useState("");
  const [selectedDay, setSelectedDay] = useState(() => getLocalDateInTimezone(new Date(), getEffectiveTimezone(preferences.timezoneMode, preferences.fixedTimezone)));
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const timezone = getEffectiveTimezone(preferences.timezoneMode, preferences.fixedTimezone);
  const today = getLocalDateInTimezone(new Date(), timezone);

  const openDatePicker = () => {
    const input = dateInputRef.current;
    if (!input) {
      return;
    }
    try {
      input.showPicker();
    } catch {
      input.focus();
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [nextCups, nextEntries, nextWeights, recommendation, goals, dailyStats] = await Promise.all([
        listCups(),
        listAllDrinkEntries(),
        listAllWeightEntries().catch(() => []),
        getHydrationRecommendation(),
        getHydrationGoals().catch(() => []),
        listAllDailyStats().catch(() => [])
      ]);
      setCups(nextCups);
      setEntries(nextEntries);
      setWeights(nextWeights);
      syncWeightInputForDay(nextWeights, selectedDay, preferences.weightUnit, setWeightValue, timezone);
      setGoalMl(
        dailyStats.find((stat) => normalizeApiLocalDate(stat.local_date) === selectedDay)?.goal_ml ?? readLatestGoal(goals) ?? recommendation.goal_ml ?? 2500
      );
    } finally {
      setLoading(false);
    }
  }, [preferences.weightUnit, selectedDay, timezone]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectedEntries = useMemo(() => {
    return entries.filter((entry) => getEntryLocalDate(entry, timezone) === selectedDay);
  }, [entries, selectedDay, timezone]);
  const cupsById = useMemo(() => new Map(cups.map((cup) => [cup.id, cup])), [cups]);

  const totalMl = selectedEntries.reduce((sum, entry) => sum + getEntryAmountMl(entry), 0);
  const lastWeight = useMemo(() => findLatestWeight(weights), [weights]);
  const selectedWeight = useMemo(() => findLatestWeight(weights.filter((weight) => getWeightLocalDate(weight, timezone) === selectedDay)), [selectedDay, timezone, weights]);
  const weightSource = selectedWeight ?? lastWeight;
  const weightSourceDate = weightSource ? getWeightLocalDate(weightSource, timezone) : undefined;
  const lastWeightHint =
    weightSourceDate && weightSourceDate !== selectedDay ? t("today.weightLastEntered", { date: formatDateLabel(weightSourceDate, locale) }) : undefined;

  const saveCup = async (values: { name: string; volumeMl: number; icon: string }) => {
    try {
      if (editingCup) {
        const updated = await updateCup(editingCup.id, {
          name: values.name,
          volume_ml: values.volumeMl,
          icon: values.icon
        });
        setCups((current) => current.map((cup) => (cup.id === editingCup.id && updated ? updated : cup)));
      } else {
        const created = await createCup({ name: values.name, volume_ml: values.volumeMl, icon: values.icon });
        if (created) {
          setCups((current) => [...current, created]);
        } else {
          await refresh();
        }
      }
      setEditorOpen(false);
      setEditingCup(null);
    } catch (error) {
      handleError(error, translateError, showToast);
    }
  };

  const removeCup = async (cup: Cup) => {
    if (!window.confirm(t("today.deleteCup"))) {
      return;
    }
    try {
      await deleteCup(cup.id);
      setCups((current) => current.filter((item) => item.id !== cup.id));
      setEditorOpen(false);
      setEditingCup(null);
    } catch (error) {
      handleError(error, translateError, showToast);
    }
  };

  const recordCup = async (cup: Cup) => {
    const idempotencyKey = createIdempotencyKey();
    const tempId = `optimistic-${idempotencyKey}`;
    const entryTime = getDefaultEntryTime(selectedDay, today, timezone);
    const optimisticEntry: DrinkEntry = {
      id: tempId,
      amount_ml: cup.volume_ml ?? 0,
      cup_id: cup.id,
      cup_icon_snapshot: cup.icon ?? "cup",
      entry_time: entryTime,
      timezone,
      local_date: selectedDay,
      optimistic: true
    };
    setEntries((current) => [optimisticEntry, ...current]);
    try {
      const created = await recordDrinkFromCup({
        cup,
        timezoneMode: preferences.timezoneMode,
        fixedTimezone: preferences.fixedTimezone,
        entryTime,
        idempotencyKey
      });
      if (created) {
        setEntries((current) => current.map((entry) => (entry.id === tempId ? { ...created, local_date: created.local_date ?? selectedDay } : entry)));
        showToast({
          title: t("today.added", { amount: formatVolume(cup.volume_ml ?? 0, preferences.volumeUnit) }),
          actionLabel: t("today.undo"),
          onAction: async () => {
            await deleteDrinkEntry(created.id);
            setEntries((current) => current.filter((entry) => entry.id !== created.id));
            showToast({ title: t("today.undone") });
          }
        });
      } else {
        await refresh();
        showToast({ title: t("today.added", { amount: formatVolume(cup.volume_ml ?? 0, preferences.volumeUnit) }) });
      }
    } catch (error) {
      setEntries((current) => current.filter((entry) => entry.id !== tempId));
      handleError(error, translateError, showToast);
    }
  };

  const saveEntry = async (amountMl: number, note: string, time: string) => {
    if (!editingEntry) {
      return;
    }
    try {
      const nextEntryTime = zonedDateTimeToIso(selectedDay, time, timezone);
      const previousEntryTime = editingEntry.entry_time ?? "";
      if (previousEntryTime && formatTimeInputInTimezone(previousEntryTime, timezone) !== time) {
        const replacement = await createDrinkEntry({
          amount_ml: amountMl,
          cup_id: editingEntry.cup_id ?? undefined,
          entry_time: nextEntryTime,
          timezone,
          note
        });
        await deleteDrinkEntry(editingEntry.id);
        if (replacement) {
          setEntries((current) =>
            current.map((entry) => (entry.id === editingEntry.id ? preserveEntryVesselSnapshot({ ...replacement, local_date: selectedDay }, editingEntry) : entry))
          );
        } else {
          await refresh();
        }
      } else {
        const updated = await updateDrinkEntry(editingEntry.id, { amount_ml: amountMl, note });
        if (updated) {
          setEntries((current) => current.map((entry) => (entry.id === editingEntry.id ? preserveEntryVesselSnapshot(updated, editingEntry) : entry)));
        } else {
          await refresh();
        }
      }
      setEditingEntry(null);
    } catch (error) {
      handleError(error, translateError, showToast);
    }
  };

  const removeEntry = async (entry: DrinkEntry): Promise<boolean> => {
    try {
      await deleteDrinkEntry(entry.id);
      setEntries((current) => current.filter((item) => item.id !== entry.id));
      showToast({
        title: t("today.entryDeleted"),
        actionLabel: t("today.restore"),
        onAction: async () => {
          try {
            const restored = await restoreDrinkEntry(entry, timezone);
            if (restored) {
              setEntries((current) => [preserveEntryVesselSnapshot(restored, entry), ...current]);
            }
          } catch (error) {
            handleError(error, translateError, showToast);
          }
        }
      });
      return true;
    } catch (error) {
      handleError(error, translateError, showToast);
      return false;
    }
  };

  const addWeight = async () => {
    if (!weightValue) {
      return;
    }
    try {
      const created = await createWeightEntry({
        weight_kg: weightToKg(Number(weightValue), preferences.weightUnit),
        entry_time: getDefaultEntryTime(selectedDay, today, timezone),
        timezone,
        note: ""
      });
      if (created) {
        setWeights((current) => [created, ...current.filter((weight) => weight.id !== created.id)]);
        setWeightValue(formatWeightInput(created.weight_kg ?? 0, preferences.weightUnit));
      } else {
        await refresh();
      }
      showToast({ title: t("common.done") });
    } catch (error) {
      handleError(error, translateError, showToast);
    }
  };

  return (
    <>
      <div className="daily-screen stack">
        <section className="daily-sticky" aria-label={t("today.progress")}>
          <div className="day-selector">
            <IconButton label={t("today.previousDay")} variant="ghost" onClick={() => setSelectedDay(addDaysToDateInput(selectedDay, -1))}>
              <ChevronLeft size={18} aria-hidden="true" />
            </IconButton>
            <div className="day-selector__date-control">
              <span className="day-selector__label" aria-hidden="true">
                <CalendarDays size={16} aria-hidden="true" />
                <span>{formatSelectedDayLabel(selectedDay, today, locale, t)}</span>
              </span>
              <input
                ref={dateInputRef}
                className="day-selector__picker"
                aria-label={`${t("today.selectDate")}: ${formatSelectedDayLabel(selectedDay, today, locale, t)}`}
                type="date"
                max={today}
                value={selectedDay}
                onClick={openDatePicker}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    openDatePicker();
                  }
                }}
                onChange={(event) => {
                  const nextDay = event.target.value;
                  if (nextDay && !isFutureDateInput(nextDay, today)) {
                    setSelectedDay(nextDay);
                  }
                }}
              />
            </div>
            <IconButton
              label={t("today.nextDay")}
              variant="ghost"
              disabled={compareDateInputs(selectedDay, today) >= 0}
              onClick={() => setSelectedDay((current) => addDaysToDateInput(current, 1))}
            >
              <ChevronRight size={18} aria-hidden="true" />
            </IconButton>
          </div>
          <div className="daily-progress-line">
            <strong>{formatVolume(totalMl, preferences.volumeUnit)}</strong>
            <span>/ {formatVolume(goalMl, preferences.volumeUnit)}</span>
          </div>
          <ProgressBar value={totalMl} max={goalMl} />
        </section>

        {loading ? (
          <Skeleton rows={5} />
        ) : (
          <>
            <section className="daily-cups stack">
              <h2>{t("today.cups")}</h2>
              {cups.length ? (
                <div className="cup-row" aria-label={t("today.cups")}>
                  {cups.map((cup) => (
                    <CupButton
                      key={cup.id}
                      cup={cup}
                      label={cup.name ?? t("common.unknown")}
                      onDrink={(selectedCup) => void recordCup(selectedCup)}
                      onEdit={(selectedCup) => {
                        setEditingCup(selectedCup);
                        setEditorOpen(true);
                      }}
                    />
                  ))}
                  <CupButton label={t("today.addCup")} onCreate={() => setEditorOpen(true)} />
                </div>
              ) : (
                <EmptyState title={t("today.noCups")} action={<Button onClick={() => setEditorOpen(true)}>{t("today.addCup")}</Button>} />
              )}
            </section>

            <section className="panel stack">
              <h2>{t("today.entries")}</h2>
              {selectedEntries.length ? (
                <div className="entry-tile-grid" aria-label={t("today.entries")}>
                  {selectedEntries.map((entry) => (
                    <button
                      className={`entry-tile ${entry.optimistic ? "entry-tile--pending" : ""}`}
                      disabled={entry.optimistic}
                      key={entry.id}
                      onClick={() => setEditingEntry(entry)}
                      type="button"
                      aria-label={t("today.editEntry", { amount: formatVolume(getEntryAmountMl(entry), preferences.volumeUnit) })}
                    >
                      <VesselIcon kind={resolveEntryVesselKind(entry, cupsById)} />
                      <strong>{formatVolume(getEntryAmountMl(entry), preferences.volumeUnit)}</strong>
                      <span>{formatTimeLabel(entry.entry_time, locale)}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <EmptyState title={t("today.noEntries")} />
              )}
            </section>

            <section className="panel stack">
              <h2>{t("today.weight")}</h2>
              <div className="grid-two">
                <InputField
                  label={t("today.weight")}
                  type="number"
                  min={1}
                  value={weightValue}
                  hint={lastWeightHint}
                  onChange={(event) => setWeightValue(event.target.value)}
                />
                <Button onClick={() => void addWeight()}>{t("today.addWeight")}</Button>
              </div>
            </section>

            {preferences.showStreaks ? (
              <section className="panel">
                <div className="metric">
                  <span>{t("today.streak")}</span>
                  <strong>{computeStreak(entries, timezone)}</strong>
                </div>
              </section>
            ) : null}
          </>
        )}
      </div>

      <CupEditor
        open={editorOpen}
        cup={editingCup}
        onClose={() => {
          setEditorOpen(false);
          setEditingCup(null);
        }}
        onSave={saveCup}
        onDelete={removeCup}
      />
      <DrinkEntryEditor
        entry={editingEntry}
        timezone={timezone}
        onClose={() => setEditingEntry(null)}
        onSave={saveEntry}
        onDelete={async (entry) => {
          const removed = await removeEntry(entry);
          if (removed) {
            setEditingEntry(null);
          }
        }}
      />
    </>
  );
}

function DrinkEntryEditor({
  entry,
  timezone,
  onClose,
  onSave,
  onDelete
}: {
  entry: DrinkEntry | null;
  timezone: string;
  onClose: () => void;
  onSave: (amountMl: number, note: string, time: string) => Promise<void>;
  onDelete: (entry: DrinkEntry) => Promise<void>;
}) {
  const { t } = useI18n();
  const [amountMl, setAmountMl] = useState(getEntryAmountMl(entry) || 250);
  const [note, setNote] = useState(entry?.note ?? "");
  const [time, setTime] = useState("");

  useEffect(() => {
    setAmountMl(getEntryAmountMl(entry) || 250);
    setNote(entry?.note ?? "");
    setTime(entry?.entry_time ? formatTimeInputInTimezone(entry.entry_time, timezone) : "12:00");
  }, [entry, timezone]);

  return (
    <Modal
      open={Boolean(entry)}
      title={t("common.edit")}
      onClose={onClose}
      footer={
        <>
          {entry ? (
            <Button variant="danger" onClick={() => void onDelete(entry)}>
              {t("common.delete")}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => void onSave(amountMl, note, time)}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="form-grid">
        <InputField label={t("today.cupVolume")} type="number" min={1} value={amountMl} onChange={(event) => setAmountMl(Number(event.target.value))} />
        <InputField label={t("today.time")} type="time" value={time} onChange={(event) => setTime(event.target.value)} />
        <TextAreaField label={t("today.note")} value={note} onChange={(event) => setNote(event.target.value)} />
      </div>
    </Modal>
  );
}

function getEntryLocalDate(entry: DrinkEntry, timezone: string): string {
  const localDate = normalizeApiLocalDate(entry.local_date);
  if (localDate) {
    return localDate;
  }
  if (entry.entry_time) {
    return getLocalDateInTimezone(new Date(entry.entry_time), timezone);
  }
  return getLocalDateInTimezone(new Date(), timezone);
}

function getEntryAmountMl(entry: DrinkEntry | null): number {
  return entry?.hydration_amount_ml ?? entry?.amount_ml ?? 0;
}

function getWeightLocalDate(weight: WeightEntry, timezone: string): string {
  const localDate = normalizeApiLocalDate(weight.local_date);
  if (localDate) {
    return localDate;
  }
  if (weight.entry_time) {
    return getLocalDateInTimezone(new Date(weight.entry_time), timezone);
  }
  return getLocalDateInTimezone(new Date(), timezone);
}

function findLatestWeight(weights: WeightEntry[]): WeightEntry | null {
  return [...weights].sort((left, right) => readWeightTimestamp(right) - readWeightTimestamp(left))[0] ?? null;
}

function syncWeightInputForDay(weights: WeightEntry[], selectedDay: string, unit: "kg" | "lb", setValue: (value: string) => void, timezone: string): void {
  const selectedWeight = findLatestWeight(weights.filter((weight) => getWeightLocalDate(weight, timezone) === selectedDay));
  const fallbackWeight = findLatestWeight(weights);
  const value = selectedWeight ?? fallbackWeight;
  setValue(value?.weight_kg ? formatWeightInput(value.weight_kg, unit) : "");
}

function formatWeightInput(valueKg: number, unit: "kg" | "lb"): string {
  const value = weightFromKg(valueKg, unit);
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10);
}

function readWeightTimestamp(weight: WeightEntry): number {
  const value = weight.entry_time ?? weight.created_at ?? "";
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

async function restoreDrinkEntry(entry: DrinkEntry, timezone: string): Promise<DrinkEntry | null> {
  return createDrinkEntry({
    amount_ml: getEntryAmountMl(entry),
    cup_id: entry.cup_id ?? undefined,
    entry_time: entry.entry_time,
    timezone: entry.timezone ?? timezone,
    note: entry.note ?? ""
  });
}

function getDefaultEntryTime(selectedDay: string, today: string, timezone: string): string {
  return selectedDay === today ? toIsoNow() : zonedDateTimeToIso(selectedDay, "12:00", timezone);
}

function formatSelectedDayLabel(selectedDay: string, today: string, locale: string, t: (key: string) => string): string {
  if (selectedDay === today) {
    return t("today.title");
  }
  if (selectedDay === yesterdayInputValue(today)) {
    return t("today.yesterday");
  }
  return formatDateLabel(selectedDay, locale);
}

function computeStreak(entries: DrinkEntry[], timezone: string): string {
  const dates = new Set(entries.map((entry) => getEntryLocalDate(entry, timezone)));
  let streak = 0;
  const current = new Date();
  while (dates.has(getLocalDateInTimezone(current, timezone))) {
    streak += 1;
    current.setDate(current.getDate() - 1);
  }
  return String(streak);
}

function readLatestGoal(goals: Array<{ goal_ml?: number }>): number | undefined {
  return goals.find((goal) => Boolean(goal.goal_ml))?.goal_ml;
}

function handleError(error: unknown, translateError: (code: string) => string, showToast: (toast: ToastOptions) => string): void {
  if (error instanceof ApiRequestError) {
    showToast({ title: translateError(error.code), tone: "error" });
  } else {
    showToast({ title: translateError("INTERNAL_ERROR"), tone: "error" });
  }
}
